import { getAdpSessionNode } from "@/features/adp/hooks/useAdpSessionStore";
import type { LoadTypeFormData } from "@/features/adp/schemas/LoadType";
import type { OutputPreviewFormValues } from "@/features/adp/schemas/OutputPreview";
import type { PhysicalTablesRequest } from "@/features/adp/services/destination/destination.types";
import { isOutputPreviewStringType } from "./outputPreview.utils";

type DestinationConfigurationStateSlice = {
  values?: Record<string, string>;
  submitted_destination_payload?: Record<string, unknown>;
  data_source_instance?: string;
};

function readDestinationConfigurationState(
  nodeId: string,
): DestinationConfigurationStateSlice | null {
  const node = getAdpSessionNode(nodeId) as
    | { destinationConfigurationState?: DestinationConfigurationStateSlice }
    | null;
  return node?.destinationConfigurationState ?? null;
}

/**
 * Database object for physical-tables API: form-backed fields from destination configuration.
 * Prefers `submitted_destination_payload` (includes `type`); otherwise merges `values` with `type` from instance.
 */
export function getDestinationDatabaseRecordForPhysicalTable(
  nodeId: string,
): Record<string, unknown> | null {
  const state = readDestinationConfigurationState(nodeId);
  if (!state) return null;

  if (state.submitted_destination_payload && typeof state.submitted_destination_payload === "object") {
    return { ...state.submitted_destination_payload };
  }

  const type = state.data_source_instance?.trim() ?? "";
  if (!type || !state.values) return null;

  return {
    type,
    ...state.values,
  };
}

export function mapLoadTypeToCdcType(
  loadType: LoadTypeFormData["loadType"],
): PhysicalTablesRequest["cdc_type"] {
  return loadType === "full" ? "fullload" : "incremental";
}

export function mapScdTypeFormToApi(scdType: string): PhysicalTablesRequest["scd_type"] {
  return scdType.trim().toUpperCase() === "SCD1" ? "scd_1" : "scd_2";
}

function toComparableColumnName(value?: string): string {
  return value?.trim().toLowerCase() ?? "";
}

export type BuildPhysicalTablePayloadInput = {
  destinationDatabase: Record<string, unknown>;
  loadTypeForm: LoadTypeFormData;
  outputPreviewValues: OutputPreviewFormValues;
};

/**
 * Builds `PhysicalTablesRequest` for POST /target-data-sources/physical-tables.
 * Omits `ref_type` and `is_feature`. Column flags `delete_identifier`, `created_date`, `modified_date` are false until wired to load-type columns.
 */
export function buildPhysicalTableCreatePayload(
  input: BuildPhysicalTablePayloadInput,
): PhysicalTablesRequest {
  const { destinationDatabase, loadTypeForm, outputPreviewValues } = input;
  const isIncrementalLoad = loadTypeForm.loadType === "incremental";
  const deleteIdentifierColumn = toComparableColumnName(loadTypeForm.deleteIdentifier);
  const createdDateColumn = toComparableColumnName(loadTypeForm.createdDate);
  const modifiedDateColumn = toComparableColumnName(loadTypeForm.modifiedDate);

  const databases = [{ ...destinationDatabase }] as PhysicalTablesRequest["databases"];

  const columns: PhysicalTablesRequest["columns"] = outputPreviewValues.columns
    .filter((c) => c.selected)
    .map((c) => {
      const columnName = toComparableColumnName(c.fieldName);
      const matchesDeleteIdentifier =
        isIncrementalLoad && Boolean(deleteIdentifierColumn) && columnName === deleteIdentifierColumn;
      const matchesCreatedDate =
        isIncrementalLoad && Boolean(createdDateColumn) && columnName === createdDateColumn;
      const matchesModifiedDate =
        isIncrementalLoad && Boolean(modifiedDateColumn) && columnName === modifiedDateColumn;

      return {
        column_name: c.fieldName,
        column_data_type: c.fieldDataType,
        column_length: String(
          isOutputPreviewStringType(c.fieldDataType) ? c.fieldLength : 0,
        ),
        is_primary_key: c.isPrimaryKey,
        delete_identifier: matchesDeleteIdentifier,
        created_date: matchesCreatedDate,
        modified_date: matchesModifiedDate,
      };
    });

  return {
    databases,
    columns,
    cdc_type: mapLoadTypeToCdcType(loadTypeForm.loadType),
    scd_type: mapScdTypeFormToApi(loadTypeForm.scdType),
  };
}
